import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import { RichTextField, cleanHtmlForSave } from "../components/resourcePerson/RichTextField";
import { StringListEditor, TableOfContentsEditor } from "../components/resourcePerson/MetadataEditors";
import UploadField from "../components/resourcePerson/UploadField";
import { useResourcePersonMarketplace } from "../store/resourcePerson/resourcePersonStore";
import { MARKETPLACE_TYPES } from "../store/resourcePerson/resourcePersonMarketplaceStore";
import {
  parseTableOfContents,
  linesToArray,
  ACADEMIC_LEVELS,
  FILE_FORMATS,
} from "../utils/resourceMetadata";

const TYPE_LABELS = {
  "project-topics": "Project Topic",
  "seminar-topics": "Seminar Topic",
  "research-materials": "Research Material",
  manuals: "Manual",
};

const EMPTY_FORM = {
  title: "",
  summary: "",
  description: "",
  abstract: "",
  chapterOnePreview: "",
  tableOfContents: "",
  whatIsIncluded: "",
  academicLevel: "",
  fieldOfStudy: "",
  fileFormat: "",
  price: "",
  coverImage: "",
  fileUrl: "",
  filePublicId: "",
  fileCloudType: "",
  fileDeliveryType: "",
};

const fieldClass =
  "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/10";
const labelClass = "block text-xs font-semibold uppercase tracking-wide text-slate-500";

export default function ResourcePersonMarketplaceCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = MARKETPLACE_TYPES.includes(searchParams.get("type"))
    ? searchParams.get("type")
    : MARKETPLACE_TYPES[0];
  const { createMarketplaceItem, marketplaceMutationLoading, marketplaceMutationError } =
    useResourcePersonMarketplace();

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const set = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("A title is required.");
      return;
    }

    let tableOfContents;
    try {
      tableOfContents = parseTableOfContents(form.tableOfContents);
    } catch (err) {
      setError(err.message);
      return;
    }

    const summary = cleanHtmlForSave(form.summary);
    const description = cleanHtmlForSave(form.description);
    const abstract = cleanHtmlForSave(form.abstract);
    const chapterOnePreview = cleanHtmlForSave(form.chapterOnePreview);

    const payload = {
      title: form.title.trim(),
      ...(summary ? { summary } : {}),
      ...(description ? { description } : {}),
      ...(abstract ? { abstract } : {}),
      ...(chapterOnePreview ? { chapterOnePreview } : {}),
      ...(tableOfContents.length ? { tableOfContents } : {}),
      ...(linesToArray(form.whatIsIncluded).length
        ? { whatIsIncluded: linesToArray(form.whatIsIncluded) }
        : {}),
      ...(form.academicLevel ? { academicLevel: form.academicLevel } : {}),
      ...(form.fieldOfStudy.trim() ? { fieldOfStudy: form.fieldOfStudy.trim() } : {}),
      ...(form.fileFormat ? { fileFormat: form.fileFormat } : {}),
      ...(form.fileUrl.trim() ? { fileUrl: form.fileUrl.trim() } : {}),
      ...(form.filePublicId.trim() ? { filePublicId: form.filePublicId.trim() } : {}),
      ...(form.fileCloudType.trim() ? { fileCloudType: form.fileCloudType.trim() } : {}),
      ...(form.fileDeliveryType.trim()
        ? { fileDeliveryType: form.fileDeliveryType.trim() }
        : {}),
      ...(form.coverImage.trim() ? { coverImage: form.coverImage.trim() } : {}),
      ...(form.price !== "" && !Number.isNaN(Number(form.price))
        ? { price: Number(form.price) }
        : {}),
    };

    const result = await createMarketplaceItem(type, payload);
    if (!result.success) {
      setError(result.error || "Could not create this item.");
      return;
    }
    navigate(`/resource-person/marketplace?type=${encodeURIComponent(type)}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <ResourcePersonNavBar title="Marketplace" subTitle="New item" />
      <main className="mx-auto max-w-4xl px-4 py-6 md:px-6">
        <Link
          to={`/resource-person/marketplace?type=${encodeURIComponent(type)}`}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
        >
          <ArrowLeft className="h-4 w-4" /> Back to marketplace
        </Link>

        <form
          onSubmit={submit}
          className="mt-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-[#004ac6]">
                Marketplace
              </p>
              <h1 className="m-0 mt-1 text-2xl font-bold text-slate-900">
                New {TYPE_LABELS[type]}
              </h1>
              <p className="m-0 mt-1 text-sm text-slate-500">
                Fill in the details, then save as a draft. You can submit it for review afterwards.
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              DRAFT
            </span>
          </div>

          {(error || marketplaceMutationError) && (
            <p className="m-0 mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error || marketplaceMutationError}
            </p>
          )}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className={`${labelClass} sm:col-span-2`}>
              Title
              <input
                className={fieldClass}
                value={form.title}
                onChange={set("title")}
                placeholder="e.g. Design and implementation of a school portal"
              />
            </label>

            <div className={`${labelClass} sm:col-span-2`}>
              Summary
              <RichTextField
                className="mt-1.5"
                value={form.summary}
                minHeight={80}
                maxHeight={160}
                placeholder="A short listing summary."
                onChange={(summary) => setForm((current) => ({ ...current, summary }))}
              />
            </div>

            <div className={`${labelClass} sm:col-span-2`}>
              Description
              <RichTextField
                className="mt-1.5"
                value={form.description}
                minHeight={140}
                maxHeight={280}
                placeholder="Describe this resource. Formatting is supported."
                onChange={(description) => setForm((current) => ({ ...current, description }))}
              />
            </div>

            <div className={`${labelClass} sm:col-span-2`}>
              Abstract
              <RichTextField
                className="mt-1.5"
                value={form.abstract}
                minHeight={100}
                maxHeight={220}
                placeholder="The abstract shown with this resource."
                onChange={(abstract) => setForm((current) => ({ ...current, abstract }))}
              />
            </div>

            <div className={`${labelClass} sm:col-span-2`}>
              Chapter one preview
              <RichTextField
                className="mt-1.5"
                value={form.chapterOnePreview}
                minHeight={100}
                maxHeight={220}
                placeholder="A preview of the first chapter."
                onChange={(chapterOnePreview) =>
                  setForm((current) => ({ ...current, chapterOnePreview }))
                }
              />
            </div>

            <label className={labelClass}>
              Academic level
              <select className={fieldClass} value={form.academicLevel} onChange={set("academicLevel")}>
                {ACADEMIC_LEVELS.map((level) => (
                  <option key={level || "none"} value={level}>
                    {level || "Not set"}
                  </option>
                ))}
              </select>
            </label>

            <label className={labelClass}>
              File format
              <select className={fieldClass} value={form.fileFormat} onChange={set("fileFormat")}>
                {FILE_FORMATS.map((format) => (
                  <option key={format || "none"} value={format}>
                    {format || "Not set"}
                  </option>
                ))}
              </select>
            </label>

            <label className={labelClass}>
              Field of study
              <input className={fieldClass} value={form.fieldOfStudy} onChange={set("fieldOfStudy")} />
            </label>

            <label className={labelClass}>
              Price
              <input
                type="number"
                min="0"
                className={fieldClass}
                value={form.price}
                onChange={set("price")}
              />
            </label>

            <div className={`${labelClass} sm:col-span-2`}>
              What is included
              <StringListEditor
                value={form.whatIsIncluded}
                onChange={(whatIsIncluded) =>
                  setForm((current) => ({ ...current, whatIsIncluded }))
                }
                placeholder="e.g. Full project documentation"
              />
            </div>

            <div className={`${labelClass} sm:col-span-2`}>
              Table of contents
              <TableOfContentsEditor
                value={form.tableOfContents}
                onChange={(tableOfContents) =>
                  setForm((current) => ({ ...current, tableOfContents }))
                }
              />
            </div>

            <div className={`${labelClass} sm:col-span-2`}>
              Cover image
              <UploadField
                kind="image"
                folder="marketplace-covers"
                label=""
                accept="image/*"
                value={form.coverImage}
                onChange={(coverImage) =>
                  setForm((current) => ({ ...current, coverImage }))
                }
              />
            </div>

            <label className={labelClass}>
              File URL
              <input className={fieldClass} value={form.fileUrl} onChange={set("fileUrl")} />
            </label>

            <label className={labelClass}>
              File public ID
              <input className={fieldClass} value={form.filePublicId} onChange={set("filePublicId")} />
            </label>

            <label className={labelClass}>
              File cloud type
              <input className={fieldClass} value={form.fileCloudType} onChange={set("fileCloudType")} />
            </label>

            <label className={labelClass}>
              File delivery type
              <input
                className={fieldClass}
                value={form.fileDeliveryType}
                onChange={set("fileDeliveryType")}
              />
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
            <Link
              to={`/resource-person/marketplace?type=${encodeURIComponent(type)}`}
              className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={marketplaceMutationLoading}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#16213A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#203052] disabled:opacity-60"
            >
              {marketplaceMutationLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" /> Create draft
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
